import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faReceipt, faPlus, faTriangleExclamation, faFolderOpen,
    faCircleCheck, faPaperclip, faSpinner, faArrowLeft,
    faFileLines, faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';

import StockModal from './StockModal';

/**
 * Book the purchase into Zoho Books.
 *
 * THE ADMIN PICKS ONE THING: THE VENDOR, and types one figure: the invoice
 * total. The expense account comes from the items' own categories (one order,
 * one account — an invoice has one total and splitting it would mean inventing
 * a breakdown the invoice does not carry), the date and reference come from
 * the order, and the invoice PDF is attached automatically.
 *
 * VAT IS DERIVED, NEVER TYPED. It comes from the vendor's tax treatment in
 * Zoho: a vendor who is not VAT registered cannot have charged any, so the
 * expense carries none. The breakdown is shown as the amount is typed, next to
 * a plain instruction to check it against the invoice — because the one thing
 * a person can catch that we cannot is the invoice saying something different.
 */
/*
 * ONE EXPENSE PER SUPPLIER INVOICE (owner, 2026-09-12: "orders with 3 invoices
 * need 3 Zoho Books inputs — 3 different providers completed that one order").
 * The dialog books exactly one invoice of the order, under its own vendor. A
 * file that belongs to that same invoice (a delivery note, page two) can be
 * ticked to go up with it; nothing is ticked by default.
 */
export default function ExpenseModal({ order, invoice: invoiceProp = null, onClose, onDone, onNeedInvoice }) {
    const invoiceId = invoiceProp?.id ?? null;
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [vendor, setVendor] = useState(null);
    const [amount, setAmount] = useState('');
    /* The date on the SUPPLIER'S invoice, which is what the books should say.
       Defaults to the day the order was placed — usually the same thing, but a
       supplier who invoices on delivery dates it days later, and posting that
       to the wrong month is a reconciliation somebody does by hand at year end. */
    const [date, setDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);     // the new-vendor sub-form
    const [extra, setExtra] = useState([]);           // other files that belong to THIS invoice

    useEffect(() => {
        let alive = true;
        axios.get(`/api/stock/orders/${order.id}/expense-preview`, {
            params: invoiceId != null ? { invoice_id: invoiceId } : {},
        })
            .then((r) => {
                if (!alive) return;
                setPreview(r.data);
                setDate(r.data?.date || '');
                setLoading(false);
            })
            .catch((e) => {
                if (!alive) return;
                setError(e.response?.data?.error || 'Could not reach Zoho.');
                setLoading(false);
            });
        return () => { alive = false; };
    }, [order.id, invoiceId]);

    const account = preview?.account || {};
    const vatRate = vendor?.is_vat_registered ? (preview?.tax?.tax_percentage || 5) : 0;

    /* The invoice total is what the supplier billed — VAT included, which is
       how UAE invoices read and how the office already books these by hand. */
    const total = Number(amount) || 0;
    const vat = vatRate ? total - (total / (1 + vatRate / 100)) : 0;
    const net = total - vat;

    const money = (n) => n.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!vendor) { setError('Pick the vendor this was bought from.'); return; }
        if (!(total > 0)) { setError('Enter what the invoice came to.'); return; }
        if (!date) { setError('Give the date on the invoice.'); return; }

        setSaving(true);
        setError('');
        try {
            const res = await axios.post(`/api/stock/orders/${order.id}/expense`, {
                invoice_id: preview?.invoice?.id ?? invoiceId,
                attach_invoice_ids: extra,
                vendor_id: vendor.contact_id,
                amount: total,
                date,
            });
            await onDone(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Zoho refused that expense.');
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <StockModal title={`Record in Zoho — order #${order.id}`} onClose={onClose} width={560}>
                <p className="stk__progress">
                    <FontAwesomeIcon icon={faSpinner} spin /> Reading your Zoho settings…
                </p>
            </StockModal>
        );
    }

    if (adding) {
        return (
            <NewVendor
                onCancel={() => setAdding(false)}
                onCreated={(v) => { setVendor(v); setAdding(false); }}
                onClose={onClose}
            />
        );
    }

    // Every file on the order (oldest first), the ONE invoice this expense is
    // for, and the other files still to book.
    const documents = [...(preview?.invoices || [])].reverse();
    const invoice = preview?.invoice || null;
    const others = preview?.others || [];
    const toggleExtra = (id) => setExtra((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const attachedNames = [invoice, ...others.filter((d) => extra.includes(d.id))]
        .filter(Boolean).map((d) => d.file_name || 'file');

    /* NO INVOICE, NO EXPENSE — the server refuses it too. Recording spending
       with nothing attached is a figure nobody can substantiate later, and
       "attach it afterwards" is how it never gets attached. */
    if (!documents.length) {
        return (
            <StockModal
                title={`Record in Zoho — order #${order.id}`}
                onClose={onClose}
                width={520}
            >
                <p className="stk__warnNote">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>
                        This order has no invoice filed yet. File the supplier's invoice on it
                        first — an expense in the books needs the document behind it.
                    </span>
                </p>
                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>Close</button>
                    <button type="button" className="stk__btn stk__btn--primary" onClick={onNeedInvoice}>
                        <FontAwesomeIcon icon={faPaperclip} /> Add the invoice
                    </button>
                </div>
            </StockModal>
        );
    }

    /* Several invoices and none named, or this one already booked: the list
       is where each invoice is recorded on its own. */
    if (!invoice || preview?.already_recorded) {
        return (
            <StockModal title={`Record in Zoho — order #${order.id}`} onClose={onClose} width={520}>
                <p className="stk__warnNote">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>
                        {preview?.already_recorded
                            ? <><strong>{invoice?.file_name || 'That invoice'}</strong> is already recorded in Zoho.</>
                            : <>This order has {documents.length} invoices — each supplier invoice is its own
                                expense. Pick the one to record from the list.</>}
                    </span>
                </p>
                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>Close</button>
                    <button type="button" className="stk__btn stk__btn--primary" onClick={onNeedInvoice}>
                        <FontAwesomeIcon icon={faFileLines} /> Open the invoices
                    </button>
                </div>
            </StockModal>
        );
    }

    return (
        <StockModal
            title={`Record in Zoho — order #${order.id}`}
            subtitle={[
                preview?.invoice_of > 1 ? `Invoice ${preview.invoice_number} of ${preview.invoice_of}` : null,
                preview?.reference ? `Reference ${preview.reference}` : null,
            ].filter(Boolean).join(' · ') || undefined}
            onClose={onClose}
            width={580}
        >
            <form onSubmit={submit}>
                {/* ── Which invoice ─────────────────────────────────────── */}
                <div className="stk__field">
                    <span className="stk__fieldLabel">This invoice</span>
                    <div className="stk__invoiceRow">
                        <FontAwesomeIcon icon={faFileLines} className="stk__invoiceIcon" />
                        <span className="stk__invoiceMain">
                            <span className="stk__invoiceName">{invoice.file_name || 'Invoice'}</span>
                            <span className="stk__invoiceMeta">
                                Filed by {invoice.uploaded_by_name || 'the office'}
                            </span>
                        </span>
                        <span className="stk__linkWrap">
                            <a
                                href={invoice.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="stk__link stk__link--primary"
                            >
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                <span className="stk__linkLabel">Open</span>
                            </a>
                        </span>
                    </div>
                    {preview?.invoice_of > 1 && (
                        <span className="stk__fieldHint">
                            Each supplier invoice on this order is recorded as its own Zoho expense,
                            under its own vendor.
                        </span>
                    )}
                </div>

                {others.length > 0 && (
                    <div className="stk__field">
                        <span className="stk__fieldLabel">Part of this same invoice?</span>
                        <span className="stk__fieldHint stk__fieldHint--above">
                            Tick a file only if it belongs to this invoice — a delivery note or a second
                            page. Another supplier's invoice gets its own expense.
                        </span>
                        <div className="stk__docPick">
                            {others.map((d) => (
                                <label key={d.id} className="stk__docPickRow">
                                    <input
                                        type="checkbox"
                                        checked={extra.includes(d.id)}
                                        onChange={() => toggleExtra(d.id)}
                                    />
                                    <span>{d.file_name || 'File'}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Vendor ─────────────────────────────────────────────── */}
                <div className="stk__field">
                    <span className="stk__fieldLabel">Which vendor?</span>
                    {vendor ? (
                        <VendorCard vendor={vendor} rate={vatRate} onChange={() => setVendor(null)} />
                    ) : (
                        <VendorPicker onPick={setVendor} onAdd={() => setAdding(true)} />
                    )}
                </div>

                {/* ── What it cost ───────────────────────────────────────── */}
                <div className="stk__field">
                    <span className="stk__fieldLabel">What did the invoice come to?</span>
                    <span className="stk__fieldHint stk__fieldHint--above">
                        The total on the invoice, {vatRate ? 'VAT included' : 'with no VAT on it'}.
                    </span>
                    <input
                        type="number"
                        className="stk__input stk__amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="AED"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                    />

                    {/* THE CATEGORY IS NOT A QUESTION. It comes from the items on
                        the order, so it is stated, not chosen. */}
                    <p className="stk__accountNote">
                        <FontAwesomeIcon icon={faFolderOpen} />
                        <span>
                            Posting to <strong>{account.account_name || 'no matching account'}</strong>
                            {preview?.mixed
                                ? <> — this order covers {preview.categories.join(', ')}, so it books to the general stock account.</>
                                : <> — from {preview?.categories?.join(', ')}.</>}
                            {preview?.paid_through?.account_name
                                ? <> Paid through <strong>{preview.paid_through.account_name}</strong>.</>
                                : null}
                        </span>
                    </p>

                    {account.account_name && !account.exact && (
                        <p className="stk__warnNote">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            <span>
                                This Zoho organisation has no <strong>{account.wanted}</strong> account,
                                so it will post to <strong>{account.account_name}</strong> instead.
                            </span>
                        </p>
                    )}
                </div>

                <label className="stk__field">
                    <span className="stk__fieldLabel">Invoice date</span>
                    <input
                        type="date"
                        className="stk__input"
                        value={date}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setDate(e.target.value)}
                    />
                    <span className="stk__fieldHint">
                        The date on the supplier's invoice — this is the date the expense
                        carries in Zoho. Defaults to the day the order was placed.
                    </span>
                </label>

                {/* ── The tax breakdown ──────────────────────────────────── */}
                {total > 0 && (
                    <div className={`stk__vat ${vatRate ? '' : 'stk__vat--none'}`}>
                        <div className="stk__vatRow">
                            <span>Net</span>
                            <strong>AED {money(net)}</strong>
                        </div>
                        <div className="stk__vatRow">
                            <span>VAT {vatRate ? `${vatRate}%` : ''}</span>
                            <strong>{vatRate ? `AED ${money(vat)}` : 'none'}</strong>
                        </div>
                        <div className="stk__vatRow stk__vatRow--total">
                            <span>Total on the invoice</span>
                            <strong>AED {money(total)}</strong>
                        </div>
                        <p className="stk__vatCheck">
                            {vatRate ? (
                                <>
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <span>
                                        Please make sure the VAT amount is correct — it must match the
                                        VAT line on {vendor?.contact_name}'s invoice exactly.
                                    </span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faCircleCheck} />
                                    <span>
                                        {vendor?.contact_name} is not VAT registered in Zoho, so no VAT
                                        is claimed. If their invoice shows VAT, stop — either the
                                        invoice or their Zoho record is wrong.
                                    </span>
                                </>
                            )}
                        </p>
                    </div>
                )}

                {/* ── The attachment ─────────────────────────────────────── */}
                <p className="stk__attachNote">
                    <FontAwesomeIcon icon={faPaperclip} />
                    {attachedNames.length === 1 ? (
                        <span>
                            <strong>{attachedNames[0]}</strong> is uploaded to the expense
                            automatically — you do not need to attach it in Zoho.
                        </span>
                    ) : (
                        <span>
                            <strong>{attachedNames.join(', ')}</strong> are merged into one PDF and
                            uploaded to the expense automatically — nothing to attach in Zoho.
                        </span>
                    )}
                </p>

                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>Cancel</button>
                    <button type="submit" className="stk__btn stk__btn--primary" disabled={saving || !vendor || !(total > 0) || !date}>
                        <FontAwesomeIcon icon={saving ? faSpinner : faReceipt} spin={saving} />{' '}
                        {saving ? 'Recording…' : `Record AED ${money(total)}`}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}

/* ── Vendor picker ───────────────────────────────────────────────────────── */

/**
 * The vendor combobox.
 *
 * OPEN IT AND THE VENDORS ARE THERE. The list loads on mount, so somebody who
 * does not know how the supplier is spelled in Zoho can simply scroll — and
 * typing then searches the whole org server-side, which matters because the
 * live books hold hundreds of names like "AL MUALIM ELECT DEVICES & MOBILES
 * PHONE TR LLC" that nobody would find any other way.
 *
 * The VAT state rides in the option itself, so the consequence of the choice
 * is visible before it is made rather than after.
 */
function VendorPicker({ onPick, onAdd }) {
    const loadOptions = useCallback(async (input) => {
        try {
            const r = await axios.get('/api/stock/zoho/vendors', { params: { search: input } });
            return (r.data?.vendors || []).map((v) => ({
                value: v.contact_id,
                label: v.contact_name,
                vendor: v,
            }));
        } catch {
            return [];
        }
    }, []);

    return (
        <>
            <AsyncSelect
                cacheOptions
                defaultOptions
                autoFocus
                /* The server has already matched; react-select's own default
                   filter would then re-filter those results against the label
                   and hide anything Zoho matched on another field. */
                filterOption={null}
                loadOptions={loadOptions}
                onChange={(opt) => opt && onPick(opt.vendor)}
                placeholder="Pick a vendor, or type to search…"
                noOptionsMessage={({ inputValue }) => (
                    inputValue ? `No vendor matches “${inputValue}”` : 'No vendors in Zoho yet'
                )}
                loadingMessage={() => 'Searching Zoho…'}
                classNamePrefix="stkSelect"
                styles={{
                    control: (base, state) => ({
                        ...base,
                        minHeight: 42,
                        borderColor: state.isFocused ? '#4c7ac5' : '#e5e7eb',
                        boxShadow: state.isFocused ? '0 0 0 3px rgba(76,122,197,.12)' : 'none',
                        '&:hover': { borderColor: '#c7d2e4' },
                    }),
                    menu: (base) => ({ ...base, zIndex: 30 }),
                    option: (base, state) => ({
                        ...base,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        fontSize: 13.5,
                        backgroundColor: state.isFocused ? '#f7faff' : '#fff',
                        color: '#111827',
                    }),
                }}
                formatOptionLabel={(opt) => (
                    <>
                        <span className="stkSelect__name">{opt.label}</span>
                        <span className={opt.vendor.is_vat_registered ? 'stk__vatTag' : 'stk__vatTag stk__vatTag--off'}>
                            {opt.vendor.is_vat_registered ? 'VAT' : 'No VAT'}
                        </span>
                    </>
                )}
            />

            <button type="button" className="stk__linkish stk__linkish--add" onClick={onAdd}>
                <FontAwesomeIcon icon={faPlus} /> Add a new vendor to Zoho
            </button>
        </>
    );
}

function VendorCard({ vendor, rate, onChange }) {
    return (
        <div className="stk__pickedItem">
            <strong>{vendor.contact_name}</strong>
            <span>
                {vendor.is_vat_registered
                    ? `VAT registered${vendor.tax_reg_no ? ` · TRN ${vendor.tax_reg_no}` : ''} — ${rate}% will be claimed`
                    : 'Not VAT registered — no VAT will be claimed'}
            </span>
            <button type="button" className="stk__linkish" onClick={onChange}>Change vendor</button>
        </div>
    );
}

/* ── New vendor, without leaving ─────────────────────────────────────────── */

/**
 * The same three questions Zoho asks, and no more.
 *
 * TAX TREATMENT IS THE WHOLE POINT. It is what decides whether this vendor's
 * invoices carry VAT for the rest of time, so it is presented as a choice with
 * its consequence written next to it, not as a dropdown of Zoho's internal
 * vocabulary.
 */
function NewVendor({ onCancel, onCreated, onClose }) {
    const [name, setName] = useState('');
    const [treatment, setTreatment] = useState('vat_registered');
    const [trn, setTrn] = useState('');
    const [emirate, setEmirate] = useState('DU');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const registered = treatment !== 'vat_not_registered';

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!name.trim()) { setError('Give the vendor a name.'); return; }
        if (registered && !trn.trim()) {
            setError('A VAT-registered vendor needs their TRN — it is printed on their invoice.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const r = await axios.post('/api/stock/zoho/vendors', {
                name: name.trim(),
                tax_treatment: treatment,
                tax_reg_no: trn.trim() || null,
                emirate,
            });
            onCreated(r.data.vendor);
        } catch (err) {
            setError(err.response?.data?.error || 'Zoho refused that vendor.');
            setSaving(false);
        }
    };

    return (
        <StockModal
            title="Add a vendor to Zoho"
            subtitle="It is created in your books straight away, and picked for this expense."
            onClose={onClose}
            width={520}
        >
            <form onSubmit={submit}>
                <label className="stk__field">
                    <span className="stk__fieldLabel">Vendor name</span>
                    <input
                        type="text"
                        className="stk__input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Exactly as it reads on their invoice"
                        maxLength={200}
                        autoFocus
                    />
                </label>

                <div className="stk__field">
                    <span className="stk__fieldLabel">Are they VAT registered?</span>
                    <div className="stk__choices stk__choices--pair">
                        <button
                            type="button"
                            className={`stk__choice ${treatment === 'vat_registered' ? 'is-active' : ''}`}
                            onClick={() => setTreatment('vat_registered')}
                        >
                            <strong>VAT registered</strong>
                            <span>Their invoices charge 5% and we claim it back.</span>
                        </button>
                        <button
                            type="button"
                            className={`stk__choice ${treatment === 'vat_not_registered' ? 'is-active' : ''}`}
                            onClick={() => { setTreatment('vat_not_registered'); setTrn(''); }}
                        >
                            <strong>Not VAT registered</strong>
                            <span>Their invoices carry no VAT, and none is claimed.</span>
                        </button>
                    </div>
                </div>

                {registered && (
                    <div className="stk__fieldRow">
                        <label className="stk__field">
                            <span className="stk__fieldLabel">TRN</span>
                            <input
                                type="text"
                                className="stk__input"
                                value={trn}
                                onChange={(e) => setTrn(e.target.value)}
                                placeholder="15 digits, from their invoice"
                                maxLength={20}
                            />
                        </label>
                        <label className="stk__field">
                            <span className="stk__fieldLabel">Emirate</span>
                            <select className="stk__input" value={emirate} onChange={(e) => setEmirate(e.target.value)}>
                                <option value="DU">Dubai</option>
                                <option value="AZ">Abu Dhabi</option>
                                <option value="SH">Sharjah</option>
                                <option value="AJ">Ajman</option>
                                <option value="UQ">Umm Al Quwain</option>
                                <option value="RK">Ras Al Khaimah</option>
                                <option value="FU">Fujairah</option>
                            </select>
                        </label>
                    </div>
                )}

                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onCancel}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                    </button>
                    <button type="submit" className="stk__btn stk__btn--primary" disabled={saving}>
                        {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Creating…</> : 'Create vendor'}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}
